import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { usePositionContext } from '../contexts/PositionContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import MarketTicker from './MarketTicker';
import LivePrice from './LivePrice';
import LivePriceRight from './LivePriceRight';
import TimeframePanel from './TimeframePanel';
import TradingForm from './TradingForm';
import TradingChart from './TradingChart';
import PortfolioSummary from './PortfolioSummary';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { MarketData } from '../services/binanceService';
import { formatPrice } from '../utils/priceFormatter';
import RightPanel from './RightPanel';
import ToastContainer from './ToastContainer';
import PositionPanel from './PositionPanel';

interface Panel {
  id: string;
  title: string;
  component: React.ReactNode;
}

interface SortablePanelProps {
  panel: Panel;
}

const SortablePanel: React.FC<SortablePanelProps> = ({ panel }) => {
  const { theme } = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: panel.id });

  const style = {
    transform: transform ? CSS.Transform.toString({
      ...transform,
      x: 0, // Lock horizontal movement to 0
    }) : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        ${panel.id === 'chart' || panel.id === 'live-price' ? 'theme-bg-primary' : panel.id === 'positions' ? 'theme-bg-secondary' : 'theme-bg-primary'}
        rounded-lg
        ${panel.id === 'positions' ? 'px-2 pt-0.5 pb-2 mt-1' : 'p-0'}
        relative
        ${panel.id === 'positions' ? 'h-full' : ''}
        ${isDragging ? 'opacity-75 shadow-lg' : ''}
        border
        ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}
      `}
    >
      {/* Drag Handle - Top Right Corner */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 -right-[0.90rem] cursor-default z-10 opacity-70"
      >
        <div className="theme-text-quaternary hover:theme-text-secondary transition-colors">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 2zm0 6a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 8zm0 6a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 14z" />
          </svg>
        </div>
      </div>
      
      {/* Panel Content */}
      <div className="panel-content">
        {panel.component}
      </div>
    </div>
  );
};

const ReorderableLayout: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  // const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  const [selectedSymbol, setSelectedSymbol] = useState('ETHUSDT');
  const [timeframe, setTimeframe] = useState('1m');
  const [chartType, setChartType] = useState('candlestick');
     const [selectedMarket, setSelectedMarket] = useState('Crypto');
  const [currentPrice, setCurrentPrice] = useState('0.00');
  const [priceChange, setPriceChange] = useState('+0.00%');
  const [isPricePositive, setIsPricePositive] = useState(true);
  const [klinePrice, setKlinePrice] = useState('0.00');
  const [klineVolume, setKlineVolume] = useState(0);
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const priceRef = useRef<number | null>(null);
  const [priceColor, setPriceColor] = useState('text-black');
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(false);
  const [positionPanelHeight, setPositionPanelHeight] = useState(60); // Default height in pixels
  const [isDragging, setIsDragging] = useState(false);
  const [isPositionPanelFullscreen, setIsPositionPanelFullscreen] = useState(false);
  const [hasBeenManuallyResized, setHasBeenManuallyResized] = useState(false);
  const [timeframeIndex, setTimeframeIndex] = useState(0); // Track current timeframe position
  const [showArrows, setShowArrows] = useState(false); // Track if arrows should be shown
  const dragRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  // const { theme } = useTheme(); // This line is now redundant as theme is passed as a prop

  // Get position data for dynamic height calculation
  const { openPositions } = usePositionContext();

  // Reset manual resize state when panel is toggled programmatically
  const handlePositionPanelToggle = useCallback((expanded: boolean) => {
    if (!isDragging) {
      setHasBeenManuallyResized(false);
      setIsPositionsExpanded(expanded);
    }
  }, [isDragging]);

  // Calculate dynamic heights based on position panel state
  const calculateHeights = useMemo(() => {
    if (isPositionPanelFullscreen) {
      // Fullscreen mode - position panel takes full height from timeframe header down
      return {
        positionHeight: 'calc(100vh - 90px)', // Full viewport height minus header space
        positionMaxHeight: 'calc(100vh - 90px)'
      };
    } else if (isPositionsExpanded) {
      // Expanded mode - use the manually resized height if available, otherwise calculate default
      const availableHeight = window.innerHeight - 200; // Total available height minus headers
      const defaultExpandedHeight = Math.max(availableHeight * 0.15, 185); // 15% or minimum 185px
      const finalHeight = hasBeenManuallyResized ? positionPanelHeight : defaultExpandedHeight;
      
      return {
        positionHeight: `${finalHeight}px`,
        positionMaxHeight: `${finalHeight}px`
      };
    } else {
      // Collapsed mode - minimal position panel height
      const collapsedHeight = 60; // Just enough for headers
      
      return {
        positionHeight: `${collapsedHeight}px`,
        positionMaxHeight: `${collapsedHeight}px`
      };
    }
  }, [isPositionsExpanded, isPositionPanelFullscreen, positionPanelHeight, hasBeenManuallyResized]);

  // Drag handlers for resizing position panel
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = true;
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current) return;
    
    // Cancel previous animation frame if it exists
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!dragRef.current) return;
      
      // Calculate the available height for the position panel
      const viewportHeight = window.innerHeight;
      const headerHeight = 90; // Approximate header height
      const bottomPadding = 60; // Bottom padding
      const availableHeight = viewportHeight - headerHeight - bottomPadding;
      
      // Calculate new height based on mouse position from bottom
      const newHeight = viewportHeight - e.clientY - bottomPadding;
      
      // Set constraints
      const minHeight = 60; // Minimum height for position panel
      const maxHeight = Math.floor(viewportHeight * 0.55); // Allow up to 60% of screen height
      
      // Apply constraints and update height
      const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
      setPositionPanelHeight(constrainedHeight);
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current = false;
    setIsDragging(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Mark as manually resized and persist the dragged height
    setHasBeenManuallyResized(true);
    if (positionPanelHeight > 100) {
      setIsPositionsExpanded(true);
    } else {
      setIsPositionsExpanded(false);
    }
  }, [positionPanelHeight]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      // Add a class to body to indicate dragging state
      document.body.classList.add('resizing');
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Remove the dragging class
      document.body.classList.remove('resizing');
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('resizing');
      // Clean up animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.body.classList.remove('resizing');
    };
  }, []);

  // Use Binance market data hook for consistent data across all components
  const {
    isLoading: isMarketDataLoading,
    error: marketDataError,
    isConnected: isBinanceConnected,
    getMarketDataForSymbol,
    getMarketDataByCategory,
  } = useBinanceMarketData();

  // Get current symbol data from Binance
  const currentSymbolData = getMarketDataForSymbol(selectedSymbol);

  // Update price display when symbol data changes
  // This is the STABLE source for percentage changes - prevents flickering
  useEffect(() => {
    if (currentSymbolData) {
      setCurrentPrice(currentSymbolData.price);
      setPriceChange(currentSymbolData.change);
      setIsPricePositive(currentSymbolData.isPositive);
    }
  }, [currentSymbolData]);

  // Track price changes for color updates
  useEffect(() => {
    const numericCurrentPrice = parseFloat(currentPrice);
    if (!isNaN(numericCurrentPrice) && priceRef.current !== null) {
      if (numericCurrentPrice > priceRef.current) {
        setPriceColor('text-[#2AB7A6]');
      } else if (numericCurrentPrice < priceRef.current) {
        setPriceColor('text-[#e81d25]');
      } else {
        setPriceColor(theme === 'light' ? 'text-black' : 'text-white');
      }
    }
    // Update ref for next comparison
    priceRef.current = numericCurrentPrice;
  }, [currentPrice]);

  // Get market data for different categories
  const getMarketDataForCategory = (category: string): MarketData[] => {
    switch (category) {
      case 'Crypto':
        return getMarketDataByCategory('crypto');
      case 'Forex':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include forex pairs
        return [];
      case 'Commodities':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include commodities
        return [];
      case 'Stocks':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include stocks
        return [];
      default:
        return getMarketDataByCategory('crypto');
    }
  };

  // Debug logging for chart expand state
  useEffect(() => {
  }, [isChartExpanded]);

  // Real-time price updates will come from API responses
  // No simulation - prices are updated when real data is received

  // Function to handle kline price updates from TradingChart
  const handleKlinePriceUpdate = (formattedPrice: string, closePrice: number, volume: number) => {
    setKlinePrice(formattedPrice);
    setKlineVolume(volume);
  };



  const [leftPanelsOrder, setLeftPanelsOrder] = useState<string[]>(['markets', 'live-price', 'buy-sell']);

  // Market data for symbol selection - now using real Binance data
  const marketDataByCategory = {
    Crypto: getMarketDataForCategory('Crypto'),
    Forex: getMarketDataForCategory('Forex'),
    Commodities: getMarketDataForCategory('Commodities'),
    Stocks: getMarketDataForCategory('Stocks'),
  };

  // Get all market data for search functionality
  const allMarketData = Object.values(marketDataByCategory).flat();

  // Function to handle symbol selection and update prices
  // This also updates the STABLE percentage change data
  const handleSymbolSelect = (symbol: string) => {
    setSelectedSymbol(symbol);
    
    // Get the current price for the selected symbol from market data
    const selectedAsset = allMarketData.find((item: any) => item.symbol === symbol);
    
    if (selectedAsset) {
      // Update the current price and change to match the selected asset
      // This ensures stable percentage display when switching symbols
      setCurrentPrice(selectedAsset.price);
      setPriceChange(selectedAsset.change);
      setIsPricePositive(selectedAsset.isPositive);
    }
  };

  // Filter market data based on search (if needed)
  const getFilteredMarketData = (category: string, searchTerm: string = '') => {
    const data = marketDataByCategory[category as keyof typeof marketDataByCategory] || [];
    if (!searchTerm) return data;
    
    return data.filter(item => 
      item.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '6H', '12H', '1D', '1W', '3W', '1M', '2M', '3M', '6M', '1Y'];

  // const chartTypes = ['line', 'area', 'ranko', 'candlestick', 'heinkshi'];
  const chartTypes = ['line', 'area', 'candlestick'];  



  const [rightPanels, setRightPanels] = useState<Panel[]>([]);
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(true);

  // Right panel toggle function
  const toggleRightPanel = () => {
    setIsRightPanelVisible(prev => {
      const newValue = !prev;
      return newValue;
    });
  };

  // Make the function available globally for TradingForm to call
  useEffect(() => {
    (window as any).toggleRightPanel = toggleRightPanel;   
    return () => {
      delete (window as any).toggleRightPanel;
    };
  }, []);

  // Memoize the price update callback to prevent infinite re-renders
  const handlePriceUpdate = useCallback((price: string, change: string, isPositive: boolean) => {
    setCurrentPrice(price);
  }, []);

      // Use useEffect to set the panels after the component mounts
   useEffect(() => {
     setRightPanels([
               { 
          id: 'chart', 
          title: 'Real Time Chart', 
          component: (
            <div className="rounded-lg relative">
                                 <div className={`h-full theme-bg-primary rounded-lg relative ${
                   theme === 'light' ? 'border border-gray-100' : 'border border-gray-800'
                 }`} style={{ height: `calc(100vh - ${isPositionsExpanded ? '290px' : '170px'})` }}>
                  {/* Expand/Collapse Button - Positioned on top of chart */}
                  <button
                    onClick={() => {
                      setIsChartExpanded(prev => !prev);
                    }}
                    className="absolute top-1 right-20 z-10 p-2 rounded-lg transition-all duration-200 shadow-xs border theme-bg-quaternary hover:theme-bg-quinary theme-text-secondary hover:text-[#2AB7A6] theme-border group"
                    title={isChartExpanded ? "Collapse Chart" : "Expand Chart"}
                  >
                    {isChartExpanded ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" className="group-hover:stroke-[#2AB7A6] transition-colors duration-200" />
                      </svg>
                      
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" className="group-hover:stroke-[#2AB7A6] transition-colors duration-200" />
                      </svg>
                    )}
                  </button>
                  <TradingChart
                    symbol={selectedSymbol}
                    timeframe={timeframe}
                    chartType={chartType}
                    onPriceUpdate={handlePriceUpdate}
                    onKlinePriceUpdate={handleKlinePriceUpdate}
                    isRightPanelVisible={isRightPanelVisible}
                  />
                </div>
            </div>
          )
        },
               { 
          id: 'positions', 
          title: 'Positions Panel',
          component: (
            <PositionPanel 
              isRightPanelVisible={isRightPanelVisible} 
              onExpandedChange={setIsPositionsExpanded}
              isFullscreen={isPositionPanelFullscreen}
              onFullscreenToggle={() => {
                setIsPositionPanelFullscreen(!isPositionPanelFullscreen);
              }}
              hasBeenManuallyResized={hasBeenManuallyResized}
              positionPanelHeight={positionPanelHeight}
            />
          )
        }
     ]);
         }, [theme, selectedSymbol, timeframe, chartType, handlePriceUpdate, isChartExpanded, isRightPanelVisible, isPositionsExpanded, isPositionPanelFullscreen, hasBeenManuallyResized, positionPanelHeight]);

  // Handle responsive timeframe display and arrow navigation
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 660) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 3);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width < 1200) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 8);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width < 1400) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 10);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else {
        setShowArrows(false);
        setTimeframeIndex(0);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timeframeIndex, timeframes.length]);

  // Navigation functions
  const navigateTimeframe = (direction: 'left' | 'right') => {
    if (direction === 'left' && timeframeIndex > 0) {
      setTimeframeIndex(prev => prev - 1);
    } else if (direction === 'right') {
      const width = window.innerWidth;
      if (width < 660 && timeframeIndex < timeframes.length - 3) {
        setTimeframeIndex(prev => prev + 1);
      } else if (width >= 660 && width < 1200 && timeframeIndex < timeframes.length - 8) {
        setTimeframeIndex(prev => prev + 1);
      } else if (width >= 1200 && width < 1400 && timeframeIndex < timeframes.length - 10) {
        setTimeframeIndex(prev => prev + 1);
      }
    }
  };

  // Get visible timeframes based on current index
  const getVisibleTimeframes = () => {
    if (!showArrows) return timeframes;
    const width = window.innerWidth;
    if (width < 660) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 3);
    } else if (width < 1200) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 8);
    } else if (width < 1400) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 10);
    }
    return timeframes;
  };

  const leftPanels = useMemo<Panel[]>(() => [
    { id: 'markets', title: 'Market Tickers', component: <MarketTicker selectedMarket={selectedMarket} onMarketChange={setSelectedMarket} onSymbolSelect={handleSymbolSelect} /> },
    // { id: 'live-price', title: 'Live Price', component: <LivePrice selectedSymbol={selectedSymbol} currentPrice={currentPrice} priceChange={priceChange} isPricePositive={isPricePositive} klinePrice={klinePrice} klineChange={priceChange} klineIsPositive={isPricePositive} /> },
    { id: 'buy-sell', title: 'Buy/Sell Panel', component: <TradingForm symbol={selectedSymbol} currentPrice={currentPrice} /> }
  ], [selectedMarket, selectedSymbol, currentPrice, priceChange, isPricePositive, klinePrice]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeId = active.id as string;
      const overId = over.id as string;

      // Check if we're moving between left and right panels
      const isActiveInLeft = leftPanels.some(panel => panel.id === activeId);
      const isOverInRight = rightPanels.some(panel => panel.id === overId);
      const isActiveInRight = rightPanels.some(panel => panel.id === activeId);
      const isOverInLeft = leftPanels.some(panel => panel.id === overId);

      if ((isActiveInLeft && isOverInRight) || (isActiveInRight && isOverInLeft)) {
        // Moving between left and right panels
        if (isActiveInLeft && isOverInRight) {
          const [movedPanel] = leftPanels.filter(panel => panel.id === activeId);
          const newLeftPanelsOrder = leftPanelsOrder.filter(id => id !== activeId);
          const newRightPanels = [...rightPanels, movedPanel];
          
          setLeftPanelsOrder(newLeftPanelsOrder);
          setRightPanels(newRightPanels);
        } else {
          const [movedPanel] = rightPanels.filter(panel => panel.id === activeId);
          const newRightPanels = rightPanels.filter(panel => panel.id !== activeId);
          const newLeftPanelsOrder = [...leftPanelsOrder, activeId];
          
          setRightPanels(newRightPanels);
          setLeftPanelsOrder(newLeftPanelsOrder);
        }
      } else {
        // Reordering within the same side
        if (isActiveInLeft) {
          const oldIndex = leftPanelsOrder.findIndex(id => id === activeId);
          const newIndex = leftPanelsOrder.findIndex(id => id === overId);
          setLeftPanelsOrder(arrayMove(leftPanelsOrder, oldIndex, newIndex));
        } else if (isActiveInRight) {
          const oldIndex = rightPanels.findIndex(panel => panel.id === activeId);
          const newIndex = rightPanels.findIndex(panel => panel.id === overId);
          setRightPanels(arrayMove(rightPanels, oldIndex, newIndex));
        }
      }
    }
  };

  // Function to get asset name from symbol
  const getAssetName = (symbol: string): string => {
    const assetNames: { [key: string]: string } = {
      'EURUSD': 'EURUSD',
      'GBPUSD': 'Pound',
      'USDJPY': 'Yen',
      'USDCHF': 'Franc',
      'AUDUSD': 'Aussie',
      'USDCAD': 'Loonie',
      'NZDUSD': 'Kiwi',
      'EURGBP': 'Euro/Pound',
      'EURJPY': 'Euro/Yen',
      'GBPJPY': 'Pound/Yen',
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'ADA': 'Cardano',
      'DOT': 'Polkadot',
      'LINK': 'Chainlink',
      'LTC': 'Litecoin',
      'XRP': 'Ripple',
      'GOLD': 'Gold',
      'SILVER': 'Silver',
      'OIL': 'Oil',
      'AAPL': 'Apple',
      'GOOGL': 'Google',
      'MSFT': 'Microsoft',
      'AMZN': 'Amazon',
      'TSLA': 'Tesla',
      'META': 'Meta',
      'NVDA': 'NVIDIA',
      'NFLX': 'Netflix',
    };
    
    return assetNames[symbol] || symbol;
  };

  return (
    <div className="h-screen theme-bg-primary overflow-hidden">
      {/* Toast Container */}
      <ToastContainer />
      
      {/* Two Column Layout */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
                 <div className="flex h-screen">
                       {/* Left Column - Fixed minimum width */}
            <div className={`${isChartExpanded ? 'w-0 opacity-0 pointer-events-none p-0' : 'w-64 min-w-64 max-w-64 opacity-100 px-2 pt-2'} ${theme === 'light' ? 'theme-bg-secondary' : ''} theme-border border-r pb-[60px] space-y-2 overflow-hidden h-screen flex-shrink-0 transition-all duration-300 ease-in-out`}>
              <SortableContext
                items={leftPanelsOrder}
                strategy={verticalListSortingStrategy}
              >
                {leftPanelsOrder.map((panelId) => {
                  const panel = leftPanels.find(p => p.id === panelId);
                  return panel ? <SortablePanel key={panel.id} panel={panel} /> : null;
                })}
              </SortableContext>
            </div>

            {/* Right Column - Flexible width */}
            <div
              className={`${isChartExpanded ? 'w-full' : 'flex-1'} theme-bg-primary pl-2 pt-0 pb-[60px] h-screen flex flex-col transition-all duration-300 ease-in-out`}
            >
              {/* Content Area - Chart and Positions on left, Right Panel on right */}
              <div className="flex flex-1 min-h-0">
                {/* Left Content - Chart and Positions */}
                <div
                  className={`flex flex-col transition-all duration-300 ease-in-out pr-2`}
                  style={{ width: isRightPanelVisible ? 'calc(100% - 256px)' : '100%' }}
                >
                  {/* Timeframe Header - Now in middle panel */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2 space-y-2 lg:space-y-0">
                    {/* Left Side - Timeframe and Chart Type Options */}
                    
                    <div className='p-0 m-0'> 
                    <TimeframePanel
                      selectedTimeframe={timeframe}
                      onTimeframeChange={setTimeframe}
                      selectedChartType={chartType}
                      onChartTypeChange={setChartType}
                      isRightPanelVisible={isRightPanelVisible}
                    />
                    </div>

                    {/* Right Side - Symbol and Real-time Price */}
                    <div className='p-0 m-0'> 
{/* isRightPanelVisible &&  */}
                    {(
                      <LivePriceRight
                        selectedSymbol={selectedSymbol}
                        currentPrice={currentPrice}
                        priceChange={priceChange}
                        isPricePositive={isPricePositive}
                        priceColor={priceColor}
                      />
                    )}                      
                    </div>
                  </div>

                  {/* Chart Panel - Hidden in fullscreen mode */}
                  {!isPositionPanelFullscreen && (
                    <div 
                      className="flex-1 min-h-0 relative"
                      style={{ 
                        height: isDragging 
                          ? `calc(100% - 1px - ${positionPanelHeight}px)`
                          : `calc(100% - 1px - ${calculateHeights.positionHeight})`,
                        zIndex: isPositionPanelFullscreen ? 30 : 10, // Lower z-index for chart than position panel
                        transition: isDragging ? 'none' : 'all 300ms ease-in-out' // Disable transition during drag
                      }}
                    >
                      {rightPanels.find(p => p.id === 'chart') && (
                        <SortablePanel panel={rightPanels.find(p => p.id === 'chart')!} />
                      )}
                    </div>
                  )}
                  
                  {/* Resize Handle - Hidden in fullscreen mode */}
                  {!isPositionPanelFullscreen && (
                    <div
                      className={`resize-handle h-0.5 cursor-ns-resize transition-colors duration-200 flex items-center justify-center relative group ${
                        theme === 'light' 
                          ? 'bg-gray-100 hover:bg-gray-200' 
                          : 'bg-gray-800 hover:bg-gray-700'
                      } ${isDragging ? 'bg-blue-200 dark:bg-blue-800' : ''}`}
                      style={{ zIndex: 15 }} // Middle z-index for resize handle, between chart and position panel
                      onMouseDown={handleMouseDown}
                    >
                      <div className={`w-12 h-1 rounded ${
                        theme === 'light' 
                          ? 'bg-gray-400 group-hover:bg-gray-500' 
                          : 'bg-gray-600 group-hover:bg-gray-500'
                      } ${isDragging ? 'bg-blue-500' : ''}`}></div>
                    </div>
                  )}
                  
                  {/* Positions Panel - Full height in fullscreen, normal height otherwise */}
                  <div 
                    className={`relative ${
                      isPositionPanelFullscreen ? 'absolute top-0 left-0 right-0 bottom-0' : ''
                    }`}
                    style={{ 
                      height: isDragging 
                        ? `${positionPanelHeight}px`
                        : calculateHeights.positionHeight,
                      maxHeight: isDragging 
                        ? `${positionPanelHeight}px`
                        : calculateHeights.positionMaxHeight,
                      zIndex: isPositionPanelFullscreen ? 40 : 20,
                      transition: isDragging ? 'none' : 'all 300ms ease-in-out' // Disable transition during drag
                    }}
                  >
                    {rightPanels.find(p => p.id === 'positions') && (
                      <SortablePanel panel={rightPanels.find(p => p.id === 'positions')!} />
                    )}
                  </div>
                </div>

                {/* Right Panel - Order Book and Market Depth - Conditionally visible */}
                {isRightPanelVisible && (
                  <RightPanel
                    symbol={selectedSymbol}
                    isVisible={isRightPanelVisible}
                    onClose={() => setIsRightPanelVisible(false)}
                    selectedSymbol={selectedSymbol}
                    currentPrice={currentPrice}
                    priceChange={priceChange}
                    isPricePositive={isPricePositive}
                    priceColor={priceColor}
                  />
                )}
              </div>
            </div>
        </div>
      </DndContext>
    </div>
  );
};

export default ReorderableLayout;
